/**
 * DataMiningPage — `/reporting/data-mining`.
 *
 * Wired to:
 *   GET /v1/reporting/cohorts        — list cohort registry
 *   GET /v1/reporting/cohorts/:id    — drill-down (sample members)
 *
 * Cohort registry is seeded in
 *   prisma/reporting/db-migrations/20260509_e2e_analytics_extras.sql.
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Filter, Layers } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  getCohort,
  listCohorts,
  type CohortDetailWire,
  type CohortWire,
} from '../../lib/api/reporting';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';

export function DataMiningPage(): JSX.Element {
  const [filter, setFilter] = useState('');
  const [openCohort, setOpenCohort] = useState<CohortDetailWire | null>(null);

  const cohorts = useQuery<readonly CohortWire[], Error>({
    queryKey: ['reporting', 'cohorts'],
    queryFn: () => listCohorts(),
    retry: false,
  });

  const drill = useMutation<CohortDetailWire, Error, string>({
    mutationFn: (id: string) => getCohort(id),
    onSuccess: (data) => setOpenCohort(data),
  });

  const denied =
    cohorts.isError && cohorts.error instanceof ApiError &&
    (cohorts.error.status === 401 || cohorts.error.status === 403);

  const filtered = (cohorts.data ?? []).filter(
    (c) => filter.trim().length === 0 || c.label.toLowerCase().includes(filter.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Data mining"
        description="Cohort discovery, segmentation, and drill-down."
      />

      {denied && (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reporting/readCohorts</code> required.
        </DeferredNotice>
      )}

      <div className="bg-white border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-nipost-green" />
          <h3 className="font-semibold text-gray-900">Filter cohorts</h3>
        </div>
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="search by label"
          aria-label="Filter expression"
          data-testid="cohort-filter-input"
          className="w-full px-3 py-2 text-sm font-mono border border-border rounded-lg"
        />
      </div>

      <div className="bg-white border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Layers className="w-4 h-4 text-nipost-green" />
          <h3 className="font-semibold text-gray-900">
            Cohorts ({filtered.length}/{cohorts.data?.length ?? 0})
          </h3>
        </div>
        {cohorts.isPending ? (
          <p className="text-sm text-muted">Loading cohorts…</p>
        ) : cohorts.isError && !denied ? (
          <p className="text-sm text-red-600">{cohorts.error.message}</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted py-4 text-center">No cohorts match filter.</p>
        ) : (
          <ul className="divide-y divide-border" data-testid="cohort-list">
            {filtered.map((c) => (
              <li key={c.id} data-testid="cohort-row" className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">{c.label}</p>
                  <p className="text-xs text-muted">
                    {c.size.toLocaleString('en-US')} members ·{' '}
                    {c.growthPct >= 0 ? '+' : ''}{c.growthPct}% MoM ·{' '}
                    <code className="font-mono text-[10px]">{c.filterExpr}</code>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => drill.mutate(c.id)}
                  data-testid="cohort-open-btn"
                  disabled={drill.isPending}
                  className="px-2 py-1 text-xs font-medium text-nipost-green border border-nipost-green rounded hover:bg-nipost-green-light transition-colors disabled:opacity-50"
                >
                  {drill.isPending && drill.variables === c.id ? 'Loading…' : 'Open'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {openCohort && (
        <div className="bg-white border border-border rounded-xl p-5" data-testid="cohort-detail">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">
              {openCohort.label} ({openCohort.size.toLocaleString('en-US')})
            </h3>
            <button
              type="button"
              onClick={() => setOpenCohort(null)}
              className="text-xs font-medium text-muted hover:text-gray-900"
            >
              Close
            </button>
          </div>
          <p className="text-xs text-muted mb-3">
            Filter: <code className="font-mono">{openCohort.filterExpr}</code>
          </p>
          <ul className="divide-y divide-border">
            {openCohort.members.map((m) => (
              <li key={m.memberId} className="py-2 text-xs">
                <code className="font-mono">{m.memberId}</code> — {m.note}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
